import React, { useState, useRef, useCallback } from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { FileModel, UploadedFileType } from "@/models/FileModel.ts";
import { Patient } from "@/models/PatientModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import type { StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { suffixId } from "@/uiKit/atoms/utils/ids.ts";

export type FileManagerProps = {
  id?: string;
  /** Prefix of the hidden file input's id (`${idPrefix}-input`); defaults to `id`. */
  idPrefix?: string;
  label: string;
  uploadLabel?: string;
  defaultFile?: FileModel;
  fileType: UploadedFileType;
  patientInfo: Pick<Patient, "givenName" | "familyName">;
  onFilePicked?: (file: FileModel) => void;
  onDelete?: () => void;
  readOnly?: boolean;
  className?: string;
  classes?: StyleClasses<"root" | "button">;
};

export const FileManager: React.FC<FileManagerProps> = ({
  id,
  idPrefix = id,
  label,
  uploadLabel,
  defaultFile,
  fileType,
  patientInfo,
  onFilePicked,
  onDelete,
  readOnly = false,
  className,
  classes,
}) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  const [file, setFile] = useState<FileModel | undefined>(defaultFile);
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadFile = Queries.practitioner.useUploadFile();

  const uploadFileHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return;
    }
    const file = event.target.files[0];
    uploadFile.mutate(
      {
        file,
        type: fileType,
        patient_firstname: patientInfo.givenName,
        patient_lastname: patientInfo.familyName,
      },
      {
        onSuccess: data => {
          setFile(data);
          onFilePicked?.(data);
        },
      },
    );
  }, []);

  const handleDelete = () => {
    setFile(undefined);
    onDelete?.();
  };

  if (file) {
    return (
      <div id={id} className={cx(styles.container, className, classes?.root)}>
        <Icons.file className={cx(styles.icon, styles.initialIcon)} />
        <Typography variant="body" className={styles.label}>
          {label}
        </Typography>
        <input
          id={suffixId(idPrefix, "input")}
          type="file"
          className={styles.hidden}
          ref={inputRef}
          onChange={uploadFileHandler}
        />
        <div className={styles.actions}>
          <Button
            variant="text"
            data-action="common.file.download"
            component="a"
            href={file.url}
            target="_blank"
            className={cx(styles.button, classes?.button)}
            startIcon={<Icons.download className={styles.icon} />}
          />
          {!readOnly && (
            <Button
              variant="text"
              data-action="common.file.change"
              startIcon={<Icons.edit className={styles.icon} />}
              className={cx(styles.button, classes?.button)}
              onClick={() => inputRef.current?.click()}
            />
          )}
          {!readOnly && !!file && (
            <Button
              variant="text"
              data-action="common.file.delete"
              onClick={handleDelete}
              className={cx(styles.button, classes?.button)}
              startIcon={<Icons.trash className={styles.icon} />}
            />
          )}
        </div>
      </div>
    );
  } else {
    if (readOnly) {
      return null;
    } else {
      return (
        <div id={id} className={styles.emptyWrapper}>
          <input
            id={suffixId(idPrefix, "input")}
            type="file"
            className={styles.hidden}
            ref={inputRef}
            onChange={uploadFileHandler}
          />
          <Button
            data-action="common.file.upload"
            variant="text"
            pending={uploadFile.isPending}
            className={cx(styles.container, styles.containerEmpty, className)}
            onClick={() => inputRef.current?.click()}
          >
            <Icons.fileUpload className={cx(styles.icon, styles.initialIcon)} />
            <Typography className={cx({ [styles.uploadText]: true, [styles.hiddenText]: uploadFile.isPending })}>
              {uploadLabel ?? `${t("button.upload")} ${label}`}
            </Typography>
          </Button>
        </div>
      );
    }
  }
};

export type ReadOnlyFileManagerProps = {
  id?: string;
  idPrefix?: string;
  label: string;
  file: FileModel | undefined;
  className?: string;
  classes?: StyleClasses<"root" | "button">;
};

export const ReadOnlyFileManager: React.FC<ReadOnlyFileManagerProps> = ({ file, ...props }) => {
  return (
    <FileManager
      {...props}
      defaultFile={file}
      fileType="description"
      patientInfo={{
        givenName: "",
        familyName: "",
      }}
      readOnly
    />
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexShrink: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(6),
    width: "fit-content",
    backgroundColor: theme.palette.primary.contrastText,
    height: `calc(${FontSizes.body} * 2)`,
    borderRadius: FontSizes.body,
    paddingLeft: FontSizes.body,
    paddingRight: `calc(${FontSizes.body} / 2)`,
    overflow: "clip",
    maxWidth: "100%",
  }),
  containerEmpty: css({
    backgroundColor: theme.palette.common.white,
    paddingRight: FontSizes.body,
    backgroundImage: `url("data:image/svg+xml,<svg width='100%' height='100%' xmlns='http://www.w3.org/2000/svg'><rect width='100%' height='100%' stroke-width='1' stroke-dasharray='3 5' fill='none' rx='${FontSizes.body}' ry='${FontSizes.body}' stroke='currentColor' stroke-linecap='square' /></svg>")`,
  }),
  initialIcon: css({
    display: "flex",
    flexShrink: 0,
  }),
  label: css({
    display: "flex",
    flexShrink: 1,
    minWidth: 0,
    textWrap: "nowrap",
    textOverflow: "ellipsis",
    overflow: "clip",
  }),
  hidden: css({
    display: "none",
  }),
  emptyWrapper: css({
    display: "contents",
  }),
  hiddenText: css({
    visibility: "hidden",
  }),
  actions: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2),
  }),
  uploadText: css({
    display: "flex",
    flexShrink: 1,
    minWidth: 0,
    textWrap: "nowrap",
    textOverflow: "ellipsis",
    fontStyle: "italic",
    overflow: "clip",
  }),
  button: css({
    backgroundColor: theme.palette.common.white,
    borderRadius: "1000px",
    padding: theme.spacing(0, 4),
  }),
  icon: css({
    width: FontSizes.bodySmall,
    height: FontSizes.bodySmall,
  }),
});
